import { useApiClient } from '@klareospace/pharos-lib-front-utils';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { createMutationCallbacks } from '@klareospace/pharos-lib-web-utils';

import type { UserWithResolvedScopes } from '@klareospace/pharos-lib-types';
import { USER_STATUS } from '@klareospace/pharos-lib-types';

import { createUserApi, userKeys } from '../api';

// ─── Types ────────────────────────────────────────────────────────────────────

interface UpdateUserStatusVariables {
  id: string;
  status: typeof USER_STATUS.ACTIVE | typeof USER_STATUS.INACTIVE;
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useUpdateUserStatus(): UseMutationResult<UserWithResolvedScopes, Error, UpdateUserStatusVariables> {
  const client = useApiClient('admin-auth');
  const api = createUserApi(client);
  const queryClient = useQueryClient();
  const { t } = useTranslation('settings');

  return useMutation<UserWithResolvedScopes, Error, UpdateUserStatusVariables>({
    mutationFn: ({ id, status }: UpdateUserStatusVariables) => api.updateUser(id, { status }),
    ...createMutationCallbacks<UserWithResolvedScopes, UpdateUserStatusVariables>({
      errorKey: ({ status }) =>
        status === USER_STATUS.ACTIVE ? 'users.actions.reactivate.error' : 'users.actions.deactivate.error',
      invalidateKeys: [['current-user'], userKeys.all],
      queryClient,
      successKey: ({ status }) =>
        status === USER_STATUS.ACTIVE ? 'users.actions.reactivate.success' : 'users.actions.deactivate.success',
      t,
    }),
  });
}
